import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getLocalUser } from '../utils/authHelpers';
import { getRoleFilterInfo } from '../utils/roleFilters';
import { readCache, writeCache } from '../utils/cacheManager';

export interface DealPipelineRow {
  deal_id: string;
  created_at: string;
  client_id: string | null;
  user_id: string | null;
  lead_id: string | null;
  pipeline_stage: string | null;
  sales_person: string | null;
  channel_booked: string | null;
  updated_at: string | null;
  deal_value: number | null;
  notes: string | null;
}

export interface MeetingBookedRow {
  meeting_id: string;
  created_at: string;
  meeting_booked_at: string | null;
  meeting_date: string | null;
  meeting_time: string | null;
  sales_person: string | null;
  meeting_status: string | null;
  transcript: string | null;
  recording: string | null;
  booked_channel: string | null;
  mtg_invite_sent: boolean | null;
  mtg_reschd_invite_sent: boolean | null;
  mtg_invite_cancelled: boolean | null;
  client_id: string | null;
  user_id: string | null;
  lead_id: string | null;
}

export interface EnrichedDeal {
  deal_id: string;
  lead_id: string;
  pipeline_stage: string;
  sales_person: string | null;
  channel_booked: string | null;
  deal_value: number | null;
  notes: string | null;
  deal_created_at: string;
  deal_updated_at: string | null;

  company_name: string | null;
  company_website: string | null;
  company_linkedin: string | null;
  company_country: string | null;
  company_phone: string | null;
  industry_1: string | null;
  industry_2: string | null;
  employee_headcount: number | null;
  dm_full_name: string | null;
  dm_title: string | null;
  dm_email_1: string | null;
  dm_email_2: string | null;
  dm_mobile_1: string | null;
  dm_mobile_2: string | null;
  dm_linkedin: string | null;
  ovrall_lead_status: string | null;
  ovrall_lead_temp: string | null;
  ovrall_convo_summary: string | null;

  ca_call_attempts: number | null;
  ca_call_answered: number | null;
  ca_last_call_outcome: string | null;
  wa_messages_sent: number | null;
  wa_replies_received: number | null;
  ea_emails_sent: number | null;
  ea_replies_received: number | null;
  la_messages_sent: number | null;
  la_replies_received: number | null;

  meeting_id: string | null;
  meeting_date: string | null;
  meeting_time: string | null;
  meeting_status: string | null;
  meeting_booked_at: string | null;
  booked_channel: string | null;
  mtg_transcript: string | null;
  mtg_recording: string | null;
}

export function usePipelineDeals() {
  const [data, setData] = useState<EnrichedDeal[]>(() => {
    return (readCache('pipeline_deals') as EnrichedDeal[] | null) || [];
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async (bg = false) => {
    try {
      if (!bg) setIsLoading(true);
      setError(null);

      const roleInfo = getRoleFilterInfo();
      if (!roleInfo) {
        setData([]);
        if (!bg) setIsLoading(false);
        return;
      }

      let dealsQuery = supabase
        .from('deals_pipeline')
        .select('*')
        .eq('client_id', roleInfo.clientId);
      if (!roleInfo.isAdmin) dealsQuery = dealsQuery.eq('user_id', roleInfo.userId);
      dealsQuery = dealsQuery.order('created_at', { ascending: false });

      const { data: deals, error: dealsErr } = await dealsQuery;

      if (dealsErr) throw dealsErr;
      if (!deals || deals.length === 0) {
        if (mountedRef.current) {
          setData([]);
          writeCache('pipeline_deals', []);
        }
        if (!bg) setIsLoading(false);
        return;
      }

      const leadIds = [...new Set(deals.map(d => d.lead_id).filter(Boolean))] as string[];

      const [leadsResult, meetingsResult] = await Promise.all([
        leadIds.length > 0
          ? supabase.from('leads_tracking').select('*').in('lead_id', leadIds)
          : Promise.resolve({ data: [], error: null }),
        leadIds.length > 0
          ? supabase.from('meetings_booked').select('*').in('lead_id', leadIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (leadsResult.error) throw leadsResult.error;
      if (meetingsResult.error) throw meetingsResult.error;

      const leadsMap = new Map<string, any>();
      (leadsResult.data || []).forEach((l: any) => leadsMap.set(l.lead_id, l));

      const meetingsMap = new Map<string, any>();
      (meetingsResult.data || []).forEach((m: any) => {
        if (m.lead_id) meetingsMap.set(m.lead_id, m);
      });

      const enriched: EnrichedDeal[] = deals
        .filter(d => d.lead_id)
        .map(deal => {
          const lead = leadsMap.get(deal.lead_id!) || {};
          const meeting = meetingsMap.get(deal.lead_id!) || {};

          return {
            deal_id: deal.deal_id,
            lead_id: deal.lead_id!,
            pipeline_stage: deal.pipeline_stage || 'Demo Booked',
            sales_person: deal.sales_person,
            channel_booked: deal.channel_booked,
            deal_value: deal.deal_value ?? null,
            notes: deal.notes ?? null,
            deal_created_at: deal.created_at,
            deal_updated_at: deal.updated_at,

            company_name: lead.company_name || null,
            company_website: lead.company_website || null,
            company_linkedin: lead.company_linkedin || null,
            company_country: lead.company_country || null,
            company_phone: lead.company_phone || null,
            industry_1: lead.industry_1 || null,
            industry_2: lead.industry_2 || null,
            employee_headcount: lead.employee_headcount ?? null,
            dm_full_name: lead.dm_full_name || null,
            dm_title: lead.dm_title || null,
            dm_email_1: lead.dm_email_1 || null,
            dm_email_2: lead.dm_email_2 || null,
            dm_mobile_1: lead.dm_mobile_1 || null,
            dm_mobile_2: lead.dm_mobile_2 || null,
            dm_linkedin: lead.dm_linkedin || null,
            ovrall_lead_status: lead.ovrall_lead_status || null,
            ovrall_lead_temp: lead.ovrall_lead_temp || null,
            ovrall_convo_summary: lead.ovrall_convo_summary || null,

            ca_call_attempts: lead.ca_call_attempts ?? null,
            ca_call_answered: lead.ca_call_answered ?? null,
            ca_last_call_outcome: lead.ca_last_call_outcome || null,
            wa_messages_sent: lead.wa_messages_sent ?? null,
            wa_replies_received: lead.wa_replies_received ?? null,
            ea_emails_sent: lead.ea_emails_sent ?? null,
            ea_replies_received: lead.ea_replies_received ?? null,
            la_messages_sent: lead.la_messages_sent ?? null,
            la_replies_received: lead.la_replies_received ?? null,

            meeting_id: meeting.meeting_id || null,
            meeting_date: meeting.meeting_date || null,
            meeting_time: meeting.meeting_time || null,
            meeting_status: meeting.meeting_status || null,
            meeting_booked_at: meeting.meeting_booked_at || null,
            booked_channel: meeting.booked_channel || null,
            mtg_transcript: meeting.transcript || null,
            mtg_recording: meeting.recording || null,
          };
        });

      if (mountedRef.current) {
        setData(enriched);
        writeCache('pipeline_deals', enriched);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    } finally {
      if (mountedRef.current && !bg) setIsLoading(false);
    }
  }, []);

  const debouncedFetch = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchData(true), 500);
  }, [fetchData]);

  const updateDeal = useCallback(async (dealId: string, updates: Partial<DealPipelineRow>) => {
    const { error: err } = await supabase
      .from('deals_pipeline')
      .update({ ...updates, updated_at: new Date().toISOString() } as any)
      .eq('deal_id', dealId);
    if (err) throw err;
    await fetchData(true);
  }, [fetchData]);

  const deleteDeal = useCallback(async (dealId: string) => {
    const { error: err } = await supabase
      .from('deals_pipeline')
      .delete()
      .eq('deal_id', dealId);
    if (err) throw err;
    await fetchData(true);
  }, [fetchData]);

  const createDeal = useCallback(async (params: {
    lead_id: string;
    pipeline_stage: string;
    sales_person: string;
    deal_value?: number | null;
    notes?: string | null;
  }) => {
    const localUser = getLocalUser();
    if (!localUser?.client_id) throw new Error('Not authenticated');

    const { data: newDeal, error: insertErr } = await supabase
      .from('deals_pipeline')
      .insert({
        client_id: localUser.client_id,
        user_id: localUser.email,
        lead_id: params.lead_id,
        pipeline_stage: params.pipeline_stage,
        sales_person: params.sales_person,
        deal_value: params.deal_value ?? null,
        notes: params.notes ?? null,
        updated_at: new Date().toISOString(),
      })
      .select('deal_id')
      .maybeSingle();

    if (insertErr) throw insertErr;

    if (newDeal?.deal_id) {
      await supabase
        .from('leads_tracking')
        .update({ deal_created: 'yes', deal_id: newDeal.deal_id })
        .eq('lead_id', params.lead_id);
    }

    await fetchData(true);
    return newDeal?.deal_id;
  }, [fetchData]);

  useEffect(() => {
    mountedRef.current = true;
    const hasCache = data.length > 0;
    if (hasCache) {
      setIsLoading(false);
      fetchData(true);
    } else {
      fetchData(false);
    }

    const channel = supabase
      .channel('pipeline_deals_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'deals_pipeline' }, () => debouncedFetch())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'meetings_booked' }, () => debouncedFetch())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads_tracking' }, () => debouncedFetch())
      .subscribe();

    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
    };
  }, []);

  return { data, isLoading, error, refetch: fetchData, updateDeal, deleteDeal, createDeal };
}
